import gsap from 'gsap';
import {
    EventDispatcher, Group
} from 'three';
import { initShaderChunk } from './shaders/ShaderChunk';
import {
    SIZE_OPTIONS, SIZE_REFS_MAP, SIZES_COMMON, UNIT
} from '../../constants/sizes';
import { ThreeShell } from './ThreeShell';
import { FontsStorage } from './init/FontsStorage';
import { TextBlockAndButtonPlusDecor } from './objects/TextBlockAndButtonPlusDecor';
import { ResultTextBlockPlusShare } from './objects/ResultTextBlockPlusShare';
import { QuizActionEvent, QuizActionType } from '../../constants/QuizActionEvent';
import { tag } from '../../utils/tag';
import { Platform } from './objects/Platform';
import { collectTextures } from '../../helpers/collectTextures';
import { geometryLib, materialsLib } from '../../../data/settings';
import { TexturesStorage } from './init/TexturesStorage';
import { sign } from '../../helpers/sign';
import { Question3D } from './objects/Question3D';
import { CommentTextAndButton } from './objects/CommentTextAndButton';
import { Score3D } from './objects/Score3D';
import { GeometriesFactory } from './factories/GeometriesFactory';
import dataBasic from '../../../data/basic';

const ease = 'power4.inOut';

export class View extends EventDispatcher {
    #shell;
    #mobileUiContainer;
    #buttonTag;
    #currentButtonAction = null;
    #headingBlock;
    #axisG;
    #platform;
    #score3d;
    #isReady = false;
    #isStarted = false;
    #isResized = false;
    #hasBeenResizedOnce = false;
    #winWidth = 1;
    #orientation = SIZE_OPTIONS.LANDSCAPE;
    #tickHandler;
    #resizeHandler;
    #componentsAll = [];
    #questionId;
    #currentQuestionComp = null;
    #currentCommentComp = null;
    #currentResultComp = null;
    //
    #platformAngle = 0;
    #platformProgress = 0;
    #maxNumSteps = 15;
    #isPlatformShown = false;
    #isScoreShown = false;

    constructor(domElement, mobileUiContainer, introData) {
        super();
        initShaderChunk();

        const shell = new ThreeShell(domElement, false);
        const headingBlock = new TextBlockAndButtonPlusDecor(
            introData.title,
            introData.text,
            introData.buttonLabel,
            {
                type: QuizActionType.START
            },
            introData.decorationKey
        );
        const axisG = new Group();
        const platform = new Platform();
        const score3d = new Score3D();
        const { axisDistance } = SIZE_REFS_MAP[SIZE_OPTIONS.LANDSCAPE];

        axisG.position.z = -axisDistance;
        platform.position.y = UNIT.yScrollStepSize;
        platform.rotation.y = Math.PI * 0.15;
        score3d.position.y = UNIT.yScrollStepSize * 0.9;

        axisG.add(platform);
        axisG.add(score3d);
        shell.add(headingBlock);
        shell.add(axisG);

        this.#componentsAll = [headingBlock, score3d];

        // Mobile Button - DOM Element
        const buttonTag = tag('button', 'button', introData.buttonLabel);

        // - TODO: hide for Tab
        buttonTag.setAttribute('type', 'button');
        mobileUiContainer.appendChild(buttonTag);

        this.#shell = shell;
        this.#headingBlock = headingBlock;
        this.#mobileUiContainer = mobileUiContainer;
        this.#buttonTag = buttonTag;
        this.#axisG = axisG;
        this.#score3d = score3d;
        this.#platform = platform;
    }

    async load(progressUpdate) {
        const localProgressUpdate = (from, to, progress) => {
            progressUpdate(from + progress * (to - from));
        };

        // FONTS
        await FontsStorage.loadBMFs((progress) => {
            localProgressUpdate(0.01, 0.1, progress);
            // eslint-disable-next-line no-console
            // console.log('FontsStorage.loadBMFs', progress);
        });

        const materialsLibTextures = collectTextures(materialsLib);

        await TexturesStorage.preload(
            materialsLibTextures,
            (progress) => { localProgressUpdate(0.1, 0.49, progress); }
        );

        const geometryManifest = Object.keys(geometryLib).map((name) => ({
            name,
            ...geometryLib[name]
        }));

        await GeometriesFactory.preload(
            geometryManifest,
            (progress) => { localProgressUpdate(0.5, 0.99, progress); }
        );

        this.#onReady();
    }

    #onReady() {
        if (this.#isReady || !this.available) return;

        this.#componentsAll.forEach((comp) => {
            if (comp.waitForPreload) comp.setupPreloadedContent(this.#shell.infrastructure);
        });

        this.#platform.addObjects();

        this.#isReady = true;
    }

    start() {
        if (this.#isStarted || !this.available) return;

        this.#isStarted = true;

        this.#addResizeHandler();
        this.#update();

        this.#tickHandler = (time, delta) => {
            this.#update(time, delta / 1000);
        };
        gsap.ticker.add(this.#tickHandler);

        if (this.#componentsAll.length < 1) return;

        this._actionListener = this.#actionHandler.bind(this);
        this._clickListener = () => {
            if (!this.#currentButtonAction) return;

            this.#actionHandler({
                type: QuizActionEvent,
                action: {
                    ...this.#currentButtonAction
                }
            });
        };

        this.#componentsAll.forEach((comp) => {
            if (comp.clickable) {
                comp.addEventListener(QuizActionEvent, this._actionListener);
            }
            if (comp.hasStart) comp.start();
        });

        this.#buttonTag.addEventListener('click', this._clickListener);

        this.#setButtonAction({
            type: QuizActionType.START
        });

        this.#playStart();
    }

    #addComponent(comp, toAdd = true) {
        if (this.#isReady && comp.waitForPreload) {
            comp.setupPreloadedContent(this.#shell.infrastructure);
        }
        if (comp.clickable) {
            comp.addEventListener(QuizActionEvent, this._actionListener);
        }
        if (comp.hasResize) {
            comp.resize(
                this.#winWidth,
                window.innerHeight,
                this.#orientation,
                true,
                this.#shell.infrastructure.renderer.getPixelRatio()
            );
        }
        if (comp.hasStart) comp.start();
        this.#componentsAll.push(comp);
        if (toAdd) this.#shell.add(comp);
    }

    #removeComponent(comp) {
        if (comp.parent) comp.parent.remove(comp);

        if (comp.clickable) {
            comp.removeEventListener(QuizActionEvent, this._actionListener);
        }
        if (comp.hasStart) comp.stop();
        this.#componentsAll = this.#componentsAll.filter((obj) => obj !== comp);
    }

    showQuestion(data, index, numTotal, direction = 0) {
        const prevComp = this.#currentCommentComp;

        const tl = gsap.timeline({
            onComplete: () => {
                if (prevComp) this.#removeComponent(prevComp);
            }
        });

        this.#maxNumSteps = numTotal * 2 + 1;

        this.#stopScoreAnimation();
        this.#showPlatform(direction, tl);
        this.#realHideComment(direction, tl);
        this.#addQuestion(data, index, numTotal);
        this.#realShowQuestion(direction, tl);
        this.#clearButtonAction();

        this._tl = tl;
    }

    showComment(data, index, numTotal, direction, nextLabel = 'next') {
        const prevComp = this.#currentQuestionComp;

        const tl = gsap.timeline({
            onComplete: () => {
                if (prevComp) this.#removeComponent(prevComp);
            }
        });

        this.#showPlatform(direction, tl);
        this.#realHideQuestion(direction, tl);
        this.#addComment(data, index, numTotal, nextLabel);
        this.#realShowComment(direction, tl);
        this.#clearButtonAction();

        this._tl = tl;
    }

    hideQuestion(direction) {
        const tl = gsap.timeline({
            onComplete: () => {
                if (this.#currentQuestionComp) this.#removeComponent(this.#currentQuestionComp);
                if (this.#currentCommentComp) this.#removeComponent(this.#currentCommentComp);
                this.#currentQuestionComp = null;
                this.#currentCommentComp = null;
                this.#questionId = '';
            }
        });

        this.#realHideComment(direction, tl);
        // Just in case
        this.#realHideQuestion(direction, tl);

        this._tl = tl;
    }

    #addQuestion(data, index, numTotal) {
        if (this.#currentQuestionComp) this.#removeComponent(this.#currentQuestionComp);

        const question = new Question3D(data, index, numTotal);

        question.position.y = UNIT.yScrollStepSize;
        question.order = index;

        this.#currentQuestionComp = question;
        this.#questionId = data.id;

        this.#addComponent(question, false);
        this.#axisG.add(question);

        return question;
    }

    #realShowQuestion(direction, tl) {
        console.log('#realShowQuestion', this.#currentQuestionComp);

        if (!this.#currentQuestionComp) return;

        tl = tl || gsap.timeline();

        const { position, rotation, order } = this.#currentQuestionComp;
        const duration = 1.65 - 0.4;
        let rY = order % 2 ? 1 : -1;

        if (this.#orientation === SIZE_OPTIONS.MOBILE_LANDSCAPE) rY = -1;
        if (this.#orientation === SIZE_OPTIONS.MOBILE_PORTRAIT) rY = 0;
        rY *= Math.PI * 0.04;

        gsap.killTweensOf(position);
        gsap.killTweensOf(rotation);

        tl.addLabel('s', 0);

        if (typeof direction !== 'undefined') {

            let sign1 = sign(direction);

            if (sign1 === 0) sign1 = -0.7;

            tl.fromTo(position, {
                y: UNIT.yScrollStepSize * sign1
            }, {
                y: 0,
                duration,
                ease
            }, 's+=0.4');
            tl.fromTo(rotation, {
                y: -Math.PI * 2.5 * sign1 + rY
            }, {
                y: rY,
                duration,
                ease
            }, 's+=0.4');
        } else {
            tl.addLabel('s', 0);
            tl.to(position, {
                y: 0,
                duration,
                ease
            }, 's+=0.4');
            tl.to(rotation, {
                y: rY,
                duration,
                ease
            }, 's+=0.4');
        }
    }

    #realHideQuestion(direction, tl) {
        if (!this.#currentQuestionComp) return;

        tl = tl || gsap.timeline();

        const { position, rotation } = this.#currentQuestionComp;
        const sign1 = sign(direction);
        const duration = 1.1;

        gsap.killTweensOf(position);
        gsap.killTweensOf(rotation);
        tl.addLabel('s', 0);
        tl.to(position, {
            y: -UNIT.yScrollStepSize * sign1,
            duration,
            ease
        }, 's');
        tl.to(rotation, {
            y: Math.PI * 2.5 * sign1,
            duration,
            ease
        }, 's');
    }

    #addComment(data, index, numTotal, nextLabel) {
        if (this.#currentCommentComp) this.#removeComponent(this.#currentCommentComp);

        const comment = new CommentTextAndButton(
            data,
            index,
            numTotal,
            nextLabel,
            {
                type: QuizActionType.NEXT,
                fromId: this.#questionId
            }
        );

        comment.position.y = UNIT.yScrollStepSize;
        comment.order = index;

        this.#currentCommentComp = comment;
        this.#addComponent(comment, false);
        this.#axisG.add(comment);

        return comment;
    }

    #realShowComment(direction, tl) {
        console.log('#realShowComment', this.#currentCommentComp);

        if (!this.#currentCommentComp) return;

        tl = tl || gsap.timeline();

        const { position, rotation, order } = this.#currentCommentComp;
        let rY = order % 2 ? 1 : -1;
        const duration = 1.75;

        rY = Math.PI * (-0.02 - 0.5 * 0.04);
        if (this.#orientation === SIZE_OPTIONS.LANDSCAPE) {
            rY = Math.PI * (-0.02 + rY * 0.05);
        }
        if (this.#orientation === SIZE_OPTIONS.MOBILE_PORTRAIT) rY = 0;

        gsap.killTweensOf(position);
        gsap.killTweensOf(rotation);

        tl.addLabel('s', 0);

        if (typeof direction !== 'undefined') {
            const sign1 = sign(direction);

            tl.fromTo(position, {
                y: UNIT.yScrollStepSize * sign1
            }, {
                y: 0,
                duration,
                ease
            }, 's');
            tl.fromTo(rotation, {
                y: -Math.PI * 2.5 * sign1 + rY
            }, {
                y: rY,
                duration,
                ease
            }, 's');
        } else {
            tl.to(position, {
                y: 0,
                duration,
                ease
            }, 's');
            tl.to(rotation, {
                y: rY,
                duration,
                ease
            }, 's');
        }
    }

    #realHideComment(direction, tl) {
        if (!this.#currentCommentComp) return;

        tl = tl || gsap.timeline();

        const { position, rotation } = this.#currentCommentComp;
        const sign1 = sign(direction);
        const duration = 0.9;

        gsap.killTweensOf(position);
        gsap.killTweensOf(rotation);
        tl.addLabel('s', 0);
        tl.to(position, {
            y: -UNIT.yScrollStepSize * sign1,
            duration,
            ease
        }, 's');
        tl.to(rotation, {
            y: Math.PI * 2.5 * sign1,
            duration,
            ease
        }, 's');
    }

    #showPlatform(direction, tl) {
        const sign1 = -sign(direction);
        const { position, rotation } = this.#platform;
        let duration = 1.65; // Questions change duration

        gsap.killTweensOf(position);
        gsap.killTweensOf(rotation);

        tl.addLabel('s', 0);

        if (this.#isPlatformShown) {
            const a0 = this.#platformAngle;
            const da = sign1 * (Math.PI / 2);

            this.#platformAngle += da;
            this.#platformProgress += sign1 * (0.5 / this.#maxNumSteps); console.log(this.#platformProgress, 'this.#platformProgress');

            tl.to(position, {
                y: -UNIT.refSize * 0.03 * sign1,
                duration: 0.4,
                ease: 'sine.inOut'
            }, 's');
            tl.to(position, {
                y: 0,
                duration: duration - 0.4,
                ease: 'sine.outOut',
                onComplete: () => {
                    this.#loopPlatform(direction);
                }
            }, 's+=0.4');

            tl.to(rotation, {
                y: a0 - da * 0.02,
                duration: 0.4,
                ease: 'sine.inOut'
            }, 's');
            tl.to(rotation, {
                y: this.#platformAngle,
                duration: duration - 0.4,
                ease: 'sine.outOut'
            }, 's+=0.4');

            tl.to(this.#shell.bgObject3D, {
                progress: this.#platformProgress,
                duration,
                ease: 'elastic.out(0.75, 0.75)'
            }, 's+=0.85');

            return;
        }

        duration = 1.15;

        // RESET
        this.#platformAngle = 0;
        this.#platformProgress = 0.5;

        tl.fromTo(position, {
            y: UNIT.yScrollStepSize * sign1
        }, {
            y: 0,
            duration,
            ease: 'elastic.out(0.75, 0.75)',
            onComplete: () => {
                this.#loopPlatform(direction);
            }
        }, 's+=0.85');
        tl.fromTo(rotation, {
            y: this.#platformAngle + Math.PI * 0.65 * sign1
        }, {
            y: this.#platformAngle,
            duration,
            ease: 'elastic.out(0.75, 0.75)'
        }, 's+=0.85');
        tl.fromTo(this.#shell.bgObject3D, {
            progress: 0
        }, {
            progress: 0.5,
            duration,
            ease: 'elastic.out(0.75, 0.75)'
        }, 's+=0.85');

        this.#isPlatformShown = true;
    }

    #loopPlatform(direction) {
        const tl = gsap.timeline({
            repeat: -1,
            yoyo: true
        });
        const sign1 = sign(direction);
        const { position, rotation } = this.#platform;
        const duration = 1.5;

        tl.addLabel('s', 0);
        tl.addLabel('m', duration);
        tl.addLabel('s2', duration * 2);
        tl.addLabel('m2', duration * 3);
        tl.fromTo(position, {
            y: 0
        }, {
            y: UNIT.refSize * 0.01 * sign1,
            duration,
            ease: 'sine.inOut'
        }, 's');
        tl.to(position, {
            y: -UNIT.refSize * 0.006 * sign1,
            duration,
            ease: 'sine.inOut'
        }, 'm');
        tl.to(position, {
            y: UNIT.refSize * 0.012 * sign1,
            duration,
            ease: 'sine.inOut'
        }, 's2');
        tl.to(position, {
            y: 0,
            duration,
            ease: 'sine.inOut'
        }, 'm2');
        tl.fromTo(rotation, {
            y: this.#platformAngle
        }, {
            y: this.#platformAngle + Math.PI * 0.004 * sign1,
            duration: duration * 1.4,
            ease: 'sine.inOnt'
        }, 's');
        tl.to(rotation, {
            y: this.#platformAngle - Math.PI * 0.001 * sign1,
            duration: duration * 1.4,
            ease: 'sine.inOut'
        }, `s+=${duration * 1.4}`);
        tl.to(rotation, {
            y: this.#platformAngle,
            duration: duration * 1.2,
            ease: 'sine.inOut'
        }, `s+=${duration * 2.8}`);
    }

    #hidePlatform(direction, tl) {
        if (!this.#isPlatformShown) return;

        const { position, rotation } = this.#platform;
        const sign1 = sign(direction);
        const duration = 1.1;

        // RESET
        this.#platformProgress = 0;

        gsap.killTweensOf(position);
        gsap.killTweensOf(rotation);
        tl.addLabel('s', 0);
        tl.to(position, {
            y: -UNIT.yScrollStepSize * sign1,
            duration,
            ease: 'elastic.in(1, 0.75)'
        }, 's');
        tl.to(rotation, {
            y: this.#platformAngle - Math.PI * 0.5 * sign1,
            duration,
            ease: 'elastic.in(1, 0.75)',
            onComplete: () => {
                // RESET
                this.#platformAngle = 0;
                this.#platformProgress = 0;
            }
        }, 's');

        tl.to(this.#shell.bgObject3D, {
            progress: 0,
            duration,
            ease: 'elastic.out(0.75, 0.75)'
        }, 's+=0.85');

        this.#isPlatformShown = false;
    }

    displayAnswerStatus(status) {
        if (this.#currentQuestionComp) {
            this.#currentQuestionComp.displayAnswerStatus(status);
        }
    }

    showResult(data, restartLabel, direction) {
        const tl = gsap.timeline();
        const result = this.#addResult(data, restartLabel);

        this.#hidePlatform(direction, tl);
        this.#stopScoreAnimation();
        this.#setButtonAction({
            type: QuizActionType.RESTART
        }, restartLabel);

        const { position } = result;
        const sign1 = sign(direction);

        gsap.killTweensOf(position);
        tl.addLabel('s', 0);
        tl.fromTo(position, {
            y: UNIT.yScrollStepSize * sign1
        }, {
            y: 0,
            duration: 0.8,
            delay: 0.8,
            ease
        }, 's');

        result.play();

        this._tl = tl;
    }

    hideResult(direction) {
        const result = this.#currentResultComp;

        if (!result) return;

        const tl = gsap.timeline({
            onComplete: () => {
                if (result) this.#removeComponent(result);
            }
        });

        const { position } = result;
        const sign1 = sign(direction);

        gsap.killTweensOf(position);
        tl.addLabel('s', 0);
        tl.to(position, {
            y: -UNIT.yScrollStepSize * sign1,
            duration: 0.8,
            ease
        }, 's');

        result.stop();

        this._tl = tl;
        this.#currentResultComp = null;
    }

    #addResult(data, restartLabel) {
        if (this.#currentResultComp) this.#removeComponent(this.#currentResultComp);

        const {
            title,
            text,
            decorationKey
        } = data;

        const comment = new ResultTextBlockPlusShare(
            title,
            text,
            restartLabel,
            {
                type: QuizActionType.RESTART
            },
            decorationKey,
            dataBasic.shareCTA
        );

        comment.position.y = UNIT.yScrollStepSize;

        this.#currentResultComp = comment;
        this.#addComponent(comment, false);
        this.#shell.add(comment);

        return comment;
    }

    #playStart() {
        const tl = gsap.timeline();
        const { position } = this.#headingBlock;

        gsap.killTweensOf(position);
        this._headingObjTl = tl.fromTo(position, {
            z: -SIZES_COMMON.camDistance * 0.1
        }, {
            z: 0,
            duration: 3.8,
            ease: 'power1.out'
        });

        this.#headingBlock.play();
    }

    hideStart() {
        const tl = gsap.timeline();
        const { position } = this.#headingBlock;

        gsap.killTweensOf(position);
        this._headingObjTl = tl.to(position, {
            y: UNIT.yScrollStepSize,
            duration: 0.8,
            ease,
            onComplete: () => {
                this.#removeComponent(this.#headingBlock);
            }
        });
    }

    showScore() {
        if (this.#isScoreShown) return;

        const tl = gsap.timeline();

        const { position } = this.#score3d;
        const duration = 0.95;

        gsap.killTweensOf(position);
        tl.fromTo(position, {
            y: UNIT.yScrollStepSize * 0.9
        }, {
            y: 0,
            duration,
            ease: 'power1.out'
        });

        this.#isScoreShown = true;
    }

    hideScore() {
        if (!this.#isScoreShown) return;

        const tl = gsap.timeline();

        const { position } = this.#score3d;
        const duration = 0.7;

        gsap.killTweensOf(position);
        tl.addLabel('s', 0);
        tl.to(position, {
            y: UNIT.yScrollStepSize * 0.9,
            duration,
            ease: 'power1.in'
        }, 's');

        this.#stopScoreAnimation();

        this.#isScoreShown = false;
    }

    updateScore(totalScore, currentIncrement, immediate) {
        this.#score3d.updateValue(totalScore, currentIncrement, immediate);
    }

    #stopScoreAnimation() {
        this.#score3d.hideAnimation();
    }

    disableOptions() {
        if (this.#currentQuestionComp) {
            this.#currentQuestionComp.disable();
            this.#currentQuestionComp.stop();
        }
    }

    #addResizeHandler() {
        this.#isResized = true;

        this.#resizeHandler = () => {
            this.#isResized = true;
        };
        window.addEventListener('resize', this.#resizeHandler);
        window.addEventListener('orientationchange', this.#resizeHandler);
    }

    #removeResizeHandler() {
        if (typeof this.#resizeHandler !== 'function') return;

        window.removeEventListener('resize', this.#resizeHandler);
        window.removeEventListener('orientationchange', this.#resizeHandler);
        this.#resizeHandler = undefined;
    }

    #actionHandler(evt) {
        this.dispatchEvent({ ...evt });
    }

    #setButtonAction(action, label) {
        const isOff = !this.#currentButtonAction;

        this.#currentButtonAction = action;

        if (!isOff) return;

        if (label) this.#buttonTag.innerHTML = label;
        this.#mobileUiContainer.classList.add('active');
    }

    #clearButtonAction() {
        this.#currentButtonAction = null;
        this.#mobileUiContainer.classList.remove('active');
    }

    #update(time, delta) {
        if (!this.#isStarted || !this.available) return;

        if (this.#isResized) {
            this.#resize(!this.#hasBeenResizedOnce);
            this.#isResized = false;
            this.#hasBeenResizedOnce = true;
        }

        if (typeof time === 'number' && typeof delta === 'number') this.#invalidate(time, delta);

        this.#shell.render();
    }

    #resize(force) {
        if (!this.available) return;

        const w = window.innerWidth;
        const h = window.innerHeight;

        this.#shell.resize(force);
        this.#winWidth = w;
        // eslint-disable-next-line no-nested-ternary
        const orientation = w > h
            ? (h < 425 ? SIZE_OPTIONS.MOBILE_LANDSCAPE : SIZE_OPTIONS.LANDSCAPE)
            : (w < 425 ? SIZE_OPTIONS.MOBILE_PORTRAIT : SIZE_OPTIONS.PORTRAIT);
        const orientationChanged = orientation !== this.#orientation;

        this.#orientation = orientation;

        const { axisDistance, axisYCorrection } = SIZE_REFS_MAP[orientation];

        this.#axisG.position.z = -axisDistance;
        this.#axisG.position.y = axisYCorrection ?? 0;

        if (this.#componentsAll.length > 0) {
            this.#componentsAll.forEach((comp) => {
                if (!comp.hasResize) return;

                comp.resize(
                    w,
                    h,
                    orientation,
                    force,
                    this.#shell.infrastructure.renderer.getPixelRatio()
                );
            });
        }

        if (orientationChanged) {
            // TO FIX THE DIFFERENCE OF ROTATION
            this.#realShowQuestion();
            this.#realShowComment();
        }
    }

    #invalidate(t, dt) {
        if (!this.available) return;

        this.#shell.invalidate(t, dt);
        this.#componentsAll.forEach((comp) => {
            if (comp.hasInvalidate) comp.invalidate(t, dt);
        });
    }

    destroy() {
        this.stop();
        this.#shell.destroy();

        if (this.#componentsAll.length < 1) return;

        this.#componentsAll.forEach((comp) => {
            comp.destroy();
        });
    }

    // DOM Keyboard Focus Handler
    handleDomChange(evt) {
        console.log('handleDomChange', evt, this);

        // AND FOCUS BUTTONS
    }

    get available() {
        return this.#shell.hasRenderer;
    }
}
